import { useState } from 'react';
import { 
  Globe, 
  ExternalLink, 
  CheckCircle2, 
  Circle,
  Upload,
  X,
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import { Separator } from '@/components/ui/separator';
import { AuthorizationRequest } from '@/lib/authorization/types';
import { 
  SubmissionLog, 
  PayerSubmissionConfig, 
  ChecklistItem,
  DEFAULT_PORTAL_CHECKLIST,
} from '@/lib/submission/types';

interface PortalSubmissionWorkflowProps {
  caseData: AuthorizationRequest;
  submissionLog: SubmissionLog;
  config: PayerSubmissionConfig | null;
  onConfirm: (data: {
    reference_id?: string;
    confirmation_number?: string;
    proof_file_path?: string;
    proof_url?: string;
    notes?: string;
  }) => Promise<void>;
  onCancel: () => void;
}

export function PortalSubmissionWorkflow({
  caseData,
  submissionLog,
  config,
  onConfirm,
  onCancel,
}: PortalSubmissionWorkflowProps) {
  const initialChecklist = submissionLog.submission_details?.checklist_items || DEFAULT_PORTAL_CHECKLIST;
  
  const [checklist, setChecklist] = useState<ChecklistItem[]>(initialChecklist);
  const [confirmationNumber, setConfirmationNumber] = useState('');
  const [proofUrl, setProofUrl] = useState('');
  const [notes, setNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const portalUrl = config?.portal_url || caseData.payer?.portal_url;
  const allRequiredComplete = checklist.filter(item => item.required).every(item => item.completed);

  const toggleChecklistItem = (id: string) => {
    setChecklist(prev => prev.map(item => 
      item.id === id 
        ? { ...item, completed: !item.completed, completed_at: !item.completed ? new Date().toISOString() : undefined }
        : item
    ));
  };

  const handleConfirm = async () => {
    setSubmitting(true);
    try {
      await onConfirm({
        confirmation_number: confirmationNumber,
        proof_url: proofUrl || undefined,
        notes: notes || undefined,
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Card className="border-blue-500/30 bg-blue-500/5">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <CardTitle className="text-base font-semibold flex items-center gap-2">
            <Globe className="h-4 w-4 text-blue-500" />
            Portal Submission Workflow
          </CardTitle>
          <Button variant="ghost" size="sm" onClick={onCancel}>
            <X className="h-4 w-4" />
          </Button>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Portal Link */}
        {portalUrl && (
          <div className="bg-background rounded-lg p-3 border">
            <Label className="text-xs text-muted-foreground">Payer Portal</Label>
            <div className="flex items-center gap-2 mt-1">
              <a 
                href={portalUrl} 
                target="_blank" 
                rel="noopener noreferrer"
                className="text-blue-500 hover:underline flex items-center gap-1 text-sm"
              >
                {portalUrl}
                <ExternalLink className="h-3 w-3" />
              </a>
            </div>
          </div>
        )}

        {/* Case Info Summary */}
        <div className="bg-background rounded-lg p-3 border">
          <Label className="text-xs text-muted-foreground">Case Information</Label>
          <div className="grid grid-cols-2 gap-2 mt-2 text-sm">
            <div>
              <span className="text-muted-foreground">Patient:</span>{' '}
              {caseData.patient ? `${caseData.patient.last_name}, ${caseData.patient.first_name}` : 'N/A'}
            </div>
            <div>
              <span className="text-muted-foreground">Member ID:</span>{' '}
              {caseData.patient?.member_id || 'N/A'}
            </div>
            <div>
              <span className="text-muted-foreground">CPT:</span>{' '}
              {caseData.cpt_codes.join(', ')}
            </div>
            <div>
              <span className="text-muted-foreground">ICD-10:</span>{' '}
              {caseData.icd10_codes.slice(0, 2).join(', ')}
              {caseData.icd10_codes.length > 2 && '...'}
            </div>
          </div>
        </div>

        <Separator />

        {/* Submission Checklist */}
        <div>
          <Label className="text-sm font-medium">Submission Checklist</Label>
          <p className="text-xs text-muted-foreground mb-3">
            Complete each step as you submit via the payer portal
          </p>
          
          <div className="space-y-2">
            {checklist.map((item) => (
              <div 
                key={item.id}
                className="flex items-center gap-3 p-2 rounded-lg hover:bg-muted/50 transition-colors"
              >
                <Checkbox
                  id={item.id}
                  checked={item.completed}
                  onCheckedChange={() => toggleChecklistItem(item.id)}
                />
                <label 
                  htmlFor={item.id}
                  className={`text-sm flex-1 cursor-pointer ${item.completed ? 'line-through text-muted-foreground' : ''}`}
                >
                  {item.label}
                  {item.required && <span className="text-red-500 ml-1">*</span>}
                </label>
                {item.completed && (
                  <CheckCircle2 className="h-4 w-4 text-emerald-500" />
                )}
              </div>
            ))}
          </div>
        </div>

        <Separator />

        {/* Confirmation Details */}
        <div className="space-y-3">
          <Label className="text-sm font-medium">Confirmation Details</Label>
          
          <div>
            <Label htmlFor="confirmation" className="text-xs">
              Portal Confirmation/Reference Number <span className="text-red-500">*</span>
            </Label>
            <Input
              id="confirmation"
              placeholder="Enter confirmation number from portal"
              value={confirmationNumber}
              onChange={(e) => setConfirmationNumber(e.target.value)}
              className="mt-1"
            />
          </div>

          <div>
            <Label htmlFor="proof" className="text-xs">
              Screenshot/Proof URL (optional)
            </Label>
            <div className="flex gap-2 mt-1">
              <Input
                id="proof"
                placeholder="Paste URL to screenshot or confirmation"
                value={proofUrl}
                onChange={(e) => setProofUrl(e.target.value)}
                className="flex-1"
              />
              <Button variant="outline" size="icon" disabled>
                <Upload className="h-4 w-4" />
              </Button>
            </div>
          </div>

          <div>
            <Label htmlFor="notes" className="text-xs">Notes (optional)</Label>
            <Textarea
              id="notes"
              placeholder="Any additional notes about this submission..."
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              className="mt-1 min-h-[60px]"
            />
          </div>
        </div>

        {/* Actions */}
        <div className="flex gap-2 pt-2">
          <Button variant="outline" onClick={onCancel} className="flex-1">
            Cancel
          </Button>
          <Button 
            onClick={handleConfirm}
            disabled={!allRequiredComplete || !confirmationNumber || submitting}
            className="flex-1"
          >
            {submitting ? 'Confirming...' : 'Confirm Submission'}
          </Button>
        </div>

        {!allRequiredComplete && (
          <p className="text-xs text-amber-500 text-center">
            Complete all required checklist items before confirming
          </p>
        )}
      </CardContent>
    </Card>
  );
}
